<script setup>
import notification from "@/utils/plugins/NotificationService";
import { computed, onMounted, ref } from 'vue';
import { Select, CloseBold, WarnTriangleFilled } from '@element-plus/icons-vue';
import { useUiStore } from "@/app/stores/uiStore";




// Services
import VehicleRequestService from '@/api/services/vehicleRequest.service';
// Icons Components
import IconVehicle from '@/components/icons/IconVehicle.vue';
import IconRestart from '@/components/icons/IconRestart.vue';

// My Components
import FuelReceiptTable from '@/components/shared/Vehicles/FuelReceiptTable.vue';

// eslint-disable-next-line no-unused-vars
// eslint-disable-next-line no-undef
const props = defineProps({
    vehicleRequest: Object,
    canEdit: {
        type: Boolean,
        default: true,
    },
    canReturn: {
        type: Boolean,
        default: false,
    },
    canCancel: {
        type: Boolean,
        default: false,
    },
    canApprove: {
        type: Boolean,
        default: false,
    },
    canReject: {
        type: Boolean,
        default: false,
    },
    canConfirm: {
        type: Boolean,
        default: false,
    },
});
// eslint-disable-next-line no-undef
const emit = defineEmits(['close', 'finish', 'return-request', 'cancel-request', 'reject-request', 'approve-request', 'confirm-request']);

const uiStore = useUiStore();

const request = computed(() => {
    if (props.vehicleRequest) {
        return props.vehicleRequest;
    }
    return null;
})

const status = computed(() => {
    if (request.value && request.value.status) {
        return request.value.status;
    }
    return null;
})

const vehicle = computed(() => {
    if (request.value && request.value.vehicle) {
        return request.value.vehicle;
    }
    return null;
})

const editAvailable = computed(() => {
    return props.canEdit;
})

const hasAvailableActions = computed(() => {
    return props.canReturn || props.canCancel || props.canApprove || props.canReject || props.canConfirm;
})

const isReturnedOrClosed = computed(() => {
    if (request.value && request.value.status) {
        return request.value.status === 'returned' || request.value.status === 'closed';
    }
    return false;
})

const getFuelType = computed(() => {
    if (vehicle.value && vehicle.value.fuelType) {
        return fuelTypeOptions.value.find(opt => opt.value === vehicle.value.fuelType)?.label;
    }
    return null;
})

const vehicleStatus = ref([
    {
        value: 'pending',
        name: 'Pendiente'
    },
    {
        value: 'approved',
        name: 'Aprobado'
    },
    {
        value: 'cancelled',
        name: 'Cancelado'
    },
    {
        value: 'rejected',
        name: 'Rechazado'
    },
    {
        value: 'closed',
        name: 'Cerrado'
    },
    {
        value: 'returned',
        name: 'Devuelto'
    },
    {
        value: 'confirmed',
        name: 'Confirmado'
    },
])


const fuelTypeOptions = ref([
    { label: 'Diesel', value: 'diesel' },
    { label: 'Gasolina', value: 'gasoline' },
    { label: 'Eléctrico', value: 'electric' },
    { label: 'Híbrido', value: 'hybrid' },
    { label: 'Híbrido enchufable', value: 'plugin_hybrid' },
]);

onMounted(() => { })

const closeDialog = () => {
    emit('close');
}

const isSuccessfulResponse = (res) => {
    return res && (res.status === 200 || res.status === 201);
}

const buildSaveParams = () => {
    const params = {
        ...request.value,
    }

    params.vehicle = request.value.vehicle?.['@id'] ?? request.value.vehicle;

    if (Array.isArray(request.value.fuelReceiptMediaObjects)) {
        params.fuelReceiptMediaObjects = request.value.fuelReceiptMediaObjects
            .map(ticket => ticket?.['@id'] ?? ticket)
            .filter(Boolean);
    }

    return params;
}

const saveCurrentRequest = async () => {
    const params = buildSaveParams();
    return isReturnedOrClosed.value
        ? VehicleRequestService.correctVehicleRequest(request.value.id, params)
        : VehicleRequestService.updateVehicleRequest(request.value.id, params);
}

const saveRequest = async () => {
    uiStore.startLoading();
    try {
        const res = await saveCurrentRequest();
        if (isSuccessfulResponse(res)) {
            notification.addNotification({
                type: 'success',
                message: 'Solicitud actualizada',
                duration: 4000
            })
            emit('finish')
            emit('close')
        }
    } finally {
        uiStore.stopLoading();
    }
}

const addTicket = (ticket) => {
    request.value.fuelReceiptMediaObjects = ticket;
}

const removeTicket = (ticket) => {
    request.value.fuelReceiptMediaObjects = ticket;
}

const emitRequestAction = (eventName) => {
    emit(eventName, request.value);
}

const finishAction = (message) => {
    notification.addNotification({
        type: 'success',
        message,
        duration: 4000
    })
    emit('finish');
    emit('close');
}

const runDirectAction = async (serviceFn, successMessage, params = null) => {
    if (!request.value?.id) {
        notification.addNotification({
            type: 'error',
            message: 'No se ha reconocido la solicitud.',
            duration: 6000
        })
        return;
    }

    uiStore.startLoading();
    try {
        const res = params === null
            ? await serviceFn(request.value.id)
            : await serviceFn(request.value.id, params);

        if (res && (res.status === 200 || res.status === 201)) {
            finishAction(successMessage);
        }
    } catch {
        notification.addNotification({
            type: 'error',
            message: 'Ha ocurrido un error al gestionar la solicitud.',
            duration: 6000
        })
    } finally {
        uiStore.stopLoading();
    }
}

const cancelRequest = () =>
    runDirectAction(VehicleRequestService.cancelVehicleRequest, 'Solicitud cancelada', { comment: '' })

const rejectRequest = () =>
    runDirectAction(VehicleRequestService.rejectVehicleRequest, 'Solicitud rechazada', { comment: '' })

const approveRequest = () =>
    runDirectAction(VehicleRequestService.approveVehicleRequest, 'Solicitud aprobada')

const confirmRequest = () =>
    confirmReception()

const confirmReception = async () => {
    if (!request.value?.id) {
        notification.addNotification({
            type: 'error',
            message: 'No se ha reconocido la solicitud.',
            duration: 6000
        })
        return;
    }

    uiStore.startLoading();
    try {
        const saveRes = await saveCurrentRequest();
        if (!isSuccessfulResponse(saveRes)) {
            notification.addNotification({
                type: 'error',
                message: 'No se ha podido guardar la solicitud. No se confirma la recepción.',
                duration: 6000
            })
            return;
        }

        const confirmRes = await VehicleRequestService.confirmVehicleRequest(request.value.id);
        if (isSuccessfulResponse(confirmRes)) {
            finishAction('Solicitud confirmada');
        }
    } catch {
        notification.addNotification({
            type: 'error',
            message: 'No se ha podido guardar la solicitud. No se confirma la recepción.',
            duration: 6000
        })
    } finally {
        uiStore.stopLoading();
    }
}

</script>

<template>
    <div class="vehicle-request-dialog">
        <div class="my-header">
            <div class="title-text ml-4">
                <IconVehicle class="iconVehicle" />
                Solicitud de vehículo
            </div>
            <img class="close-icon pointer justify-content-end" src="@/assets/images/aspa_blue_small_entrada.png" alt=""
                @click="closeDialog()">
        </div>
        <div class="row d-flex dialog-content">
            <div class="col-12 row d-flex mb-3">
                <div class="col-md-6 el-col-xs-24">
                    <label class="w-100">Coche:</label>
                    <el-input v-model="vehicle.displayName" disabled>
                    </el-input>
                </div>
                <div class="col-md-6 el-col-xs-24">
                    <label class="w-100">Matrícula:</label>
                    <el-input v-model="vehicle.licensePlate" disabled>
                    </el-input>
                </div>
                <div class="col-md-6 el-col-xs-24 mt-2">
                    <label class="w-100">Marca:</label>
                    <el-input v-model="vehicle.brand" disabled>
                    </el-input>
                </div>
                <div class="col-md-6 el-col-xs-24 mt-2">
                    <label class="w-100">Modelo:</label>
                    <el-input v-model="vehicle.model" disabled>
                    </el-input>
                </div>
                <div class="col-md-6 el-col-xs-24 mt-2">
                    <label class="w-100">Asientos:</label>
                    <el-input v-model="vehicle.totalSeats" disabled>
                    </el-input>
                </div>
                <div class="col-md-6 el-col-xs-24 mt-2" v-if="vehicle && vehicle.maxSeats">
                    <label class="w-100">Asientos máximos:</label>
                    <el-input v-model="vehicle.maxSeats" disabled>
                    </el-input>
                </div>
                <div class="col-md-6 el-col-xs-24 mt-2" v-if="getFuelType">
                    <label class="w-100">Tipo de combustible:</label>
                    <el-input v-model="getFuelType" disabled>
                    </el-input>
                </div>
                <div class="col-md-6 el-col-xs-24 mt-2">
                    <label class="w-100">Estado:</label>
                    <el-select v-model="request.status" filterable placeholder="Estado de la solicitud" disabled>
                        <el-option v-for="stat in vehicleStatus" :key="stat.value" :label="stat.name"
                            :value="stat.value">
                        </el-option>
                    </el-select>
                </div>
                <div class="col-md-6 el-col-xs-24 mt-2">
                    <label class="w-100" for="requestDateFrom">Inicio:</label>
                    <el-date-picker v-model="request.startDate" type="datetime" placeholder="Desde" id="requestDateFrom"
                        format="DD/MM/YYYY HH:mm" :clearable="false" class="mb-1 w-100"
                        :disabled="!(editAvailable && !isReturnedOrClosed)" />
                </div>
                <div class="col-md-6 el-col-xs-24 mt-2">
                    <label class="w-100" for="requestDateTo">Fin:</label>
                    <el-date-picker v-model="request.endDate" type="datetime" placeholder="Hasta" id="requestDateTo"
                        format="DD/MM/YYYY HH:mm" :clearable="false" class="mb-1 w-100"
                        :disabled="!(editAvailable && !isReturnedOrClosed)" />
                </div>
                <div class="col-md-6 el-col-xs-24 mt-3" v-if="request.closedAt">
                    <label class="w-100" for="requestDateTo">Fecha de confirmación:</label>
                    <el-date-picker v-model="request.closedAt" type="datetime" placeholder="Hasta" id="requestDateTo"
                        format="DD/MM/YYYY HH:mm" :clearable="false" class="mb-1 w-100" disabled />
                </div>
                <div class="col-md-6 el-col-xs-24 mt-3" v-if="request && request.closedBy && request.closedBy.fullName">
                    <label class="w-100">Confirmado por:</label>
                    <el-input v-model="request.closedBy.fullName" disabled>
                    </el-input>
                </div>
                <div class="col-md-6 el-col-xs-24 mt-3" v-if="request.startKm">
                    <label class="w-100" for="requestKms">Kilómetros de inicio:</label>
                    <el-input-number v-model="request.startKm" :min="1" :step="1" id="requestKms" class="w-100"
                        placeholder="Kms Inicio" :disabled="!(!editAvailable && isReturnedOrClosed)" />
                </div>
                <div class="col-md-6 el-col-xs-24 mt-3" v-if="(request.endKm || isReturnedOrClosed)">
                    <label class="w-100" for="requestKms">Kilómetros finales:</label>
                    <el-input-number v-model="request.endKm" :min="1" :step="1" id="requestKms" class="w-100"
                        placeholder="Kms Inicio" :disabled="!editAvailable" />
                </div>
                <div class="col-md-6 el-col-xs-24 mt-3" v-if="(request.requiresSeats || editAvailable) && (request.vehicle && request.vehicle.totalSeats != request.vehicle.maxSeats) ">
                    <label class="w-100" for="requestSeats">Asientos requeridos:</label>
                    <el-input-number v-model="request.seatsCount" :min="1" :step="1" id="requestSeats" class="w-100"
                        placeholder="Asientos" :disabled="!editAvailable" />
                </div>
                <div class="col-md-6 el-col-xs-24 mt-3" v-if="request.traveledKm">
                    <label class="w-100" for="requestTraveledKms">Kilómetros recorridos:</label>
                    <el-input-number v-model="request.traveledKm" :min="1" :step="1" id="requestTraveledKms"
                        class="w-100" placeholder="Kms Inicio" :disabled="!editAvailable" />
                </div>
                <div class="col-md-12 el-col-xd-24 mt-2" v-if="request.purpose || editAvailable">
                    <label class="w-100" for="requestPurpose">Motivo:</label>
                    <textarea v-model="request.purpose" label="'Motivo de la baja'" id="requestPurpose"
                        :placeholder="'Motivo de la solicitud'" class="w-100 p-2" style="height: 50px;"
                        :disabled="!editAvailable">
                        </textarea>
                </div>
                <div class="col-md-12 el-col-xd-24 mt-2" v-if="request.observations || editAvailable">
                    <label class="w-100" for="observations">Observaciones:</label>
                    <textarea v-model="request.observations" label="'Motivo de la baja'" id="observations"
                        :placeholder="'Observaciones de la solicitud'" class="w-100 p-2" style="height: 50px;"
                        :disabled="!editAvailable">
                        </textarea>
                </div>
                <div class="col-md-12 el-col-xd-24 mt-2"
                    v-if="(request.incidents || editAvailable) && status === 'closed'">
                    <label class="w-100" for="incidents">Incidentes:</label>
                    <textarea v-model="request.incidents" label="'Motivo de la baja'" id="incidents"
                        :placeholder="'Incidentes de la solicitud'" class="w-100 p-2" style="height: 50px;"
                        :disabled="!editAvailable">
                        </textarea>
                </div>
                <!-- Tickets -->
                <div class="col-md-12 el-col-xd-24 mt-4">
                    <FuelReceiptTable ref="fuelRef" :request="request" :isReturning="false" @addFile="addTicket"
                        :canEdit="editAvailable && isReturnedOrClosed" @removeFile="removeTicket" />
                </div>
            </div>
            <div class="vehicle-request-action-panel" v-if="hasAvailableActions">
                <div class="vehicle-request-action-panel__title">
                    Acciones disponibles
                </div>
                <div class="vehicle-request-action-panel__buttons">
                    <button class="btn btn_red vehicle-request-action-button" v-if="canCancel"
                        @click="cancelRequest()">
                        <CloseBold class="vehicle-request-action-button__icon" />
                        Cancelar solicitud
                    </button>

                    <button class="btn btn_red vehicle-request-action-button" v-if="canReject"
                        @click="rejectRequest()">
                        <CloseBold class="vehicle-request-action-button__icon" />
                        Rechazar solicitud
                    </button>

                    <button class="btn btn_darkblue vehicle-request-action-button vehicle-request-action-button--blue"
                        v-if="canApprove" @click="approveRequest()">
                        <Select class="vehicle-request-action-button__icon" />
                        Aprobar solicitud
                    </button>

                    <button class="btn btn_red vehicle-request-action-button" v-if="canReturn"
                        @click="emitRequestAction('return-request')">
                        <IconRestart class="vehicle-request-action-button__icon" />
                        Devolver vehículo
                    </button>

                </div>
                <div class="vehicle-request-confirm-warning" v-if="canConfirm">
                    <div class="vehicle-request-confirm-warning__header">
                        <el-icon class="vehicle-request-confirm-warning__icon">
                            <WarnTriangleFilled />
                        </el-icon>
                        <span>Acción requerida:</span>
                    </div>
                    <p class="vehicle-request-confirm-warning__text">
                        Por favor, verifica que hayas recibido el vehículo y las llaves, revisa los justificantes
                        adjuntos y confirma la recepción.
                    </p>
                    <button class="btn btn_green px-4 vehicle-request-confirm-warning__button"
                        @click="confirmRequest()">
                        ✓ Confirmar recepción de vehículo y llaves
                    </button>
                </div>
            </div>
            <div class="row mt-4">
                <div class="col d-flex justify-content-end gap-3 dialog-actions">
                    <button class="btn btn_yellow px-4 dialog-action-button" @click="closeDialog()">
                        {{ editAvailable ? 'Cancelar' : 'Cerrar' }}
                    </button>

                    <button class="btn btn_red px-4 dialog-action-button" @click="saveRequest()" v-if="editAvailable">
                        Guardar
                    </button>
                </div>
            </div>
        </div>
    </div>

</template>

<style scoped lang="scss">
:deep(.el-input.is-disabled .el-input__wrapper),
:deep(.el-select__wrapper.is-disabled),
textarea:disabled {
    background-color: var(--el-fill-color-light);
}

.my-header {
    margin-top: 2px;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
}

.iconVehicle {
    width: 40px;
    fill: $bg-blue;
}

::v-deep(.el-input-number__increase) {
    border-top-right-radius: 20px;
    border-bottom-right-radius: 20px;
}

::v-deep(.el-input-number__decrease) {
    border-top-left-radius: 20px;
    border-bottom-left-radius: 20px;
}

:deep(.el-select__wrapper.is-disabled .el-select__selected-item),
:deep(.el-select__wrapper.is-disabled),
:deep(.el-input.is-disabled .el-input__inner),
textarea:disabled {
    cursor: default;
    color: rgb(94, 94, 94) !important;
    -webkit-text-fill-color: rgb(94, 94, 94);

}

:deep(.el-select__wrapper.is-disabled .el-select__caret),
:deep(.el-input.is-disabled .el-input__icon),
:deep(.el-input-number.is-disabled .el-input-number__increase),
:deep(.el-input-number.is-disabled .el-input-number__decrease) {
    cursor: default;
}


/* Tabla tickets */
.styled-table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 1rem;
    font-family: Arial, sans-serif;
}

.styled-table td {
    padding: 8px 12px;
    border-bottom: 1px solid #eee;
    vertical-align: middle;
}

.document-name {
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: 500;
    color: #007bff;
}

.document-name:hover {
    text-decoration: underline;
}

.icon-file {
    font-size: 1.1rem;
    color: #007bff;
}

.cost-cell {
    font-weight: 600;
    color: #2a2a2a;
}

.actions-cell {
    text-align: center;
}

.dialog-content {
    margin: 0;
}

.dialog-actions {
    width: 100%;
}

.dialog-action-button {
    width: 40%;
}

.vehicle-request-action-panel {
    display: flex;
    align-items: stretch;
    justify-content: space-between;
    gap: 16px;
    flex-direction: column;
    width: 100%;
    padding: 14px 18px;
    margin-top: 8px;
    border: 1px solid rgba(71, 76, 230, 0.18);
    border-radius: 12px;
    background: #f8f7ff;
}

.vehicle-request-action-panel__title {
    color: $bg-blue;
    font-size: 16px;
    font-weight: 700;
}

.vehicle-request-action-panel__buttons {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 12px;
    flex-wrap: wrap;
}

.vehicle-request-action-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 170px;
    min-height: 38px;
    margin-left: 0 !important;
    font-weight: 700;
}

.vehicle-request-action-button--blue {
    color: #fff;
}

.vehicle-request-action-button__icon {
    width: 16px;
    height: 16px;
    margin-right: 8px;
    fill: currentColor;
}

.vehicle-request-confirm-warning {
    padding: 18px 20px;
    border: 1px solid #faad14;
    border-radius: 6px;
    background-color: #fff3cd;
}

.vehicle-request-confirm-warning__header {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #4f5968;
    font-size: 20px;
    font-weight: 800;
}

.vehicle-request-confirm-warning__icon {
    color: #faad14;
    font-size: 22px;
}

.vehicle-request-confirm-warning__text {
    margin: 18px 0 22px;
    color: #4f5968;
    font-size: 16px;
    line-height: 1.45;
}

.vehicle-request-confirm-warning__button {
    min-height: 40px;
    font-weight: 800;
}

@media (max-width: 767px) {
    .vehicle-request-dialog {
        padding: 0 2px 8px;
    }

    .my-header {
        align-items: flex-start;
        gap: 12px;
    }

    .my-header .title-text {
        margin-left: 0 !important;
        font-size: 26px;
        line-height: 1.15;
    }

    .dialog-actions {
        flex-direction: column;
    }

    .dialog-action-button {
        width: 100%;
    }

    .vehicle-request-action-panel {
        align-items: stretch;
        flex-direction: column;
    }

    .vehicle-request-action-panel__buttons {
        flex-direction: column;
        align-items: stretch;
    }

    .vehicle-request-action-button {
        width: 100%;
    }

    .vehicle-request-confirm-warning__button {
        width: 100%;
        white-space: normal;
    }
}
</style>
